import { useState } from "react";
import { motion } from "framer-motion";
import { LogIn, Loader2, Lock, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/store/AuthStore";

export function LoginPage({ onClose }) {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(username.trim(), password, remember);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#090D16] bg-grid px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm rounded-3xl border border-slate-800 bg-[#131C2E]/90 p-8 shadow-2xl shadow-black/40"
      >
        {onClose && (
          <button
            data-testid="login-close-btn"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500 shadow-lg shadow-orange-500/30">
            <span className="font-display text-2xl font-black text-slate-950">TR</span>
          </div>
          <h1 className="font-display text-2xl font-extrabold text-white">
            TR <span className="text-orange-500">BOLT</span>calc
          </h1>
          <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Team Sign In</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Username</span>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your username"
                className="h-12 rounded-xl border-slate-700 bg-slate-900/60 pl-10 text-white"
                required
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Password</span>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                type="password"
                autoComplete="current-password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl border-slate-700 bg-slate-900/60 pl-10 text-white"
                required
              />
            </div>
          </label>

          {error && (
            <p data-testid="login-error" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm font-medium text-red-400">
              {error}
            </p>
          )}

          <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-slate-300">
            <input
              type="checkbox"
              data-testid="login-remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-orange-500"
            />
            Keep me signed in
          </label>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="h-12 w-full rounded-xl bg-orange-500 text-base font-extrabold text-slate-950 hover:bg-orange-400 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="mr-2 h-5 w-5" /> Sign In</>}
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-slate-600">
          No account? Ask your admin to add you to the team.
        </p>
      </motion.div>
    </div>
  );
}
